import { SessionContext } from '@contexts/session-context';
import { UserStatus } from '@enums/user-status.enum';
import { Page } from '@models/page';
import { UserPreference } from '@models/user-preferences.model';
import { User } from '@models/user.model';
import { ScheduledMaintenance } from '@modules/notification-banner/schedule-maintenance.model';
import UserPreferenceService from '@services/user-preference.service';
import { UserService } from '@services/user.service';
import { Icon, toastify, TruncatedText } from '@testsigma/atoms';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Caraousel from './caraousel';
import ScheduledMaintenanceService from './schedule-maintenance.services';
import { SpoofingBanner } from './spoofing-banner';
import { TrailExpiryBanner } from './trail-expiry-banner';
import UserLimitBanner from './user-limit-exceeded-banner';
import { ImportAndSavePointStatus } from './imports-status';
import VersionService from '@services/version.service';
import { useQuery } from 'react-query';
import { UserType } from '@enums/user-type.enum';
import { Feature } from '@enums/feature.enum';
import { useDrifterVersionCheck } from './hooks/use-drifter-version-check';
import { useRecorderVersionCheck } from './hooks/use-recorder-version-check';
import RecorderUpgradeBanner from './recorder-upgrade-banner';
import WebinarBanner from './webinar-banner';
import BannerSkeleton from './banner-skeleton';

type ConvertHtmlToStringProps = {
  childrenHtml: string;
};

// Helper function to format UTC time to local time in the desired format
// sample input `We have planned a scheduled database maintenance on DateTime="2025:01:01 10:00:00"`
// sample output will be `We have planned a scheduled database maintenance on 3rd Jan 2025 at 4:30 PM Local Time`
const formatToLocalTime = (utcTime: string): string => {
  dayjs.extend(utc);
  dayjs.extend(advancedFormat);
  const localTime = dayjs.utc(utcTime).local();
  return ` ${localTime.format('Do MMM YYYY')} at ${localTime.format(
    'h:mm A',
  )} Local Time`;
};

const ConvertHtmlToString: React.FC<ConvertHtmlToStringProps> = ({
  childrenHtml,
}) => {
  const formattedHtmlValue = useMemo(
    () =>
      childrenHtml?.replace(
        /(\s(?:DateTime|datetime)\s*=\s*["'])([^"']+)(["'])/gi,
        (_, prefix, utcTime) => `${formatToLocalTime(utcTime)}`,
      ),
    [childrenHtml],
  );
  return (
    <div
      dangerouslySetInnerHTML={{
        __html: formattedHtmlValue,
      }}
    />
  );
};

const ScheduleNotification = ({ closeSchedule, maintenanceMessage }) => {
  return (
    <div className='w-[calc(100%-70px)] !text-unique-800 font-normal font-sans text-center relative flex items-center justify-between'>
      <TruncatedText
        value={
          <ConvertHtmlToString childrenHtml={maintenanceMessage?.message} />
        }
        className='text-md !leading-5 !font-semibold !w-[calc(100%-50px)] text-center'
      />
      <Icon
        name='close'
        onClick={() => closeSchedule(maintenanceMessage.id)}
        className='w-6 h-6 text-secondary-600 text-2xl cursor-pointer'
      />
    </div>
  );
};

interface NotificationBannerPros {
  setNotificationCount: React.Dispatch<React.SetStateAction<number>>;
}

export const NotificationBanner: React.FC<NotificationBannerPros> = ({
  setNotificationCount,
}) => {
  const { session } = useContext(SessionContext);
  const [, setCanShowSchedule] = useState(false);
  const [canShowExpiry, setCanShowExpiry] = useState(false);
  const [canShowLimitBanner, setCanShowLimitBanner] = useState(false);
  const [page] = useState(0);
  const [size] = useState(20);
  const [activeMaintenanceMessages, setActiveMaintenanceMessages]: [
    ScheduledMaintenance[],
    Dispatch<SetStateAction<ScheduledMaintenance[]>>,
  ] = useState(null);
  const [userPreference, setUserPreference]: [
    UserPreference,
    Dispatch<SetStateAction<UserPreference>>,
  ] = useState(null as UserPreference);
  const canShowUserLimitExceededBannerKey =
    session.hasCanShowUserLimitExceededBannerKey();

  const fetchScheduledMaintenances = () => {
    ScheduledMaintenanceService.getMaintenances(size, page).then((res) => {
      setActiveMaintenanceMessages(res.content);
    });
  };

  const fetchTotalUsers = () => {
    const query = `type:${UserType.PRODUCT_USER},isDeleted:false`;
    UserService.findAll(query).then((res: Page<User>) => {
      const hasWaitlistedUsers = res.content.some(
        (user) => user.status === UserStatus.Waitlist,
      );
      canShowLimitExceed(res.totalElements, hasWaitlistedUsers);
    });
  };

  const fetchUserPreference = () => {
    UserPreferenceService.show(session?.user?.id).then((res) => {
      setUserPreference(res);
    });
  };

  const canShowLimitExceed = (
    totalUsers: number,
    isWaitlistedUsers: boolean,
  ) => {
    if (
      totalUsers > session?.tenant?.subscription?.totalAllowedUsers &&
      isWaitlistedUsers &&
      (session.user.isAdmin || session.user.isSuperAdmin)
    ) {
      setCanShowLimitBanner(true);
      setNotificationCount((prev) => prev + 1);
    }
  };

  const applicationVersionId = useMemo(
    () => userPreference?.preference?.projectPreference?.versionId || undefined,
    [userPreference],
  );

  const hasImportFeature = useMemo(
    () => session.hasFeature(Feature.IMPORT_ARTEFACTS) || false,
    [session.features],
  );

  const { hasOldDrifterVersion } = useDrifterVersionCheck();
  const { isRecorderOutdated } = useRecorderVersionCheck();
  const [isRecorderBannerDismissed, setIsRecorderBannerDismissed] =
    useState(false);
  const [isWebinarBannerDismissed, setIsWebinarBannerDismissed] = useState(
    () => localStorage.getItem('webinarBannerDismissed') === 'true',
  );

  const { data: selectedApplicationStatus } = useQuery({
    refetchInterval: 30000,
    enabled: !!applicationVersionId && hasImportFeature,
    queryFn: async () => {
      try {
        if (!applicationVersionId)
          Promise.reject(`Error: applicationVersionId=${applicationVersionId}`);
        return await VersionService.checkStatus(applicationVersionId);
      } catch (error) {
        toastify(error.response.data.error, {
          type: 'error',
          hideProgressBar: false,
        });
        Promise.reject(error);
      }
    },
  });

  const onCloseLimitExceedBanner = () => {
    setCanShowLimitBanner(false);
    localStorage.setItem('canShowUserLimitExceededBanner', 'false');
  };

  const handleCloseSchedule = (id: number) => {
    userPreference.closedMaintenances = [
      ...userPreference.closedMaintenances,
      id,
    ];
    UserPreferenceService.save(userPreference).then((res) => {
      const modifiedActiveMaintenanceMessages =
        activeMaintenanceMessages.filter(
          (maintainenanceMessage) =>
            !res?.closedMaintenances.includes(maintainenanceMessage.id),
        );
      setActiveMaintenanceMessages(modifiedActiveMaintenanceMessages);
    });
  };

  const filteredMaintenanceMessages = useMemo(() => {
    return userPreference?.closedMaintenances?.length
      ? activeMaintenanceMessages?.filter(
          (maintainenanceMessage) =>
            !userPreference?.closedMaintenances.includes(
              maintainenanceMessage.id,
            ),
        )
      : activeMaintenanceMessages || [];
  }, [activeMaintenanceMessages, userPreference?.closedMaintenances]);

  const maintainenanceConfigs = filteredMaintenanceMessages
    ? filteredMaintenanceMessages?.map((maintainenanceMessage) => ({
        key: `maintenance-${maintainenanceMessage.id}`,
        condition: true as boolean,
        content: (
          <ScheduleNotification
            closeSchedule={handleCloseSchedule}
            maintenanceMessage={maintainenanceMessage}
          />
        ),
      }))
    : [];

  const canShowRecorderBanner =
    isRecorderOutdated && !isRecorderBannerDismissed;

  const canShowWebinarBanner =
    !isWebinarBannerDismissed && dayjs().isBefore(dayjs('2026-03-26'));

  const onCloseWebinarBanner = () => {
    setIsWebinarBannerDismissed(true);
    localStorage.setItem('webinarBannerDismissed', 'true');
  };

  useEffect(() => {
    if (
      session?.user?.authType == 'ADMIN_SSO' ||
      filteredMaintenanceMessages?.length > 0 ||
      canShowExpiry ||
      canShowLimitBanner ||
      selectedApplicationStatus?.['isImportInProgress'] ||
      selectedApplicationStatus?.['isSavePointRestorationInProgress'] ||
      selectedApplicationStatus?.['isSavePointCreationInProgress'] ||
      hasOldDrifterVersion ||
      canShowRecorderBanner ||
      canShowWebinarBanner
    ) {
      setNotificationCount(1);
    } else {
      setNotificationCount(0);
    }
  }, [
    session?.user,
    canShowExpiry,
    canShowLimitBanner,
    filteredMaintenanceMessages,
    selectedApplicationStatus,
    setNotificationCount,
    hasOldDrifterVersion,
    canShowRecorderBanner,
    canShowWebinarBanner,
  ]);

  useEffect(() => {
    fetchUserPreference();
    fetchScheduledMaintenances();
    if (activeMaintenanceMessages) {
      setCanShowSchedule(true);
    }
    if (session?.tenant?.subscription?.isInTrial) {
      const nextRenewalAt = session?.tenant?.subscription?.nextRenewalAt;
      const diffDays = dayjs(nextRenewalAt).diff(dayjs(), 'days', false);
      if (diffDays >= 0 && diffDays <= 7) {
        setCanShowExpiry(true);
      }
    }
  }, [session?.tenant?.subscription, page]);

  useEffect(() => {
    if (
      session?.id &&
      !canShowLimitBanner &&
      !canShowUserLimitExceededBannerKey
    ) {
      fetchTotalUsers();
    }
  }, [session?.id]);

  const bannerConfigs = [
    {
      key: 'importStatus',
      condition:
        selectedApplicationStatus &&
        (selectedApplicationStatus['isImportInProgress'] ||
          selectedApplicationStatus['isSavePointRestorationInProgress'] ||
          selectedApplicationStatus['isSavePointCreationInProgress']),
      content: (
        <ImportAndSavePointStatus
          isImporting={selectedApplicationStatus?.['isImportInProgress']}
          isSavePointInProgress={
            selectedApplicationStatus?.['isSavePointRestorationInProgress']
          }
          isSavePointCreationInProgress={
            selectedApplicationStatus?.['isSavePointCreationInProgress']
          }
        />
      ),
    },
    {
      key: 'spoofing',
      condition: ['ADMIN_SSO', 'JARVIS_GUEST_SSO'].includes(
        session?.user?.authType,
      ),
      content: <SpoofingBanner isSpoofing />,
    },
    {
      key: 'trailExpiry',
      condition: canShowExpiry,
      content: (
        <TrailExpiryBanner canShowExpiry={canShowExpiry} session={session} />
      ),
    },
    {
      key: 'userLimit',
      condition: canShowLimitBanner,
      content: <UserLimitBanner onClose={onCloseLimitExceedBanner} />,
    },
    ...maintainenanceConfigs,
    // {
    //   key: 'terminal-upgrade',
    //   condition: hasOldDrifterVersion,
    //   content: <TerminalUpgradeBanner />,
    //   className: 'bg-white !p-0',
    // },
    {
      key: 'recorder-upgrade',
      condition: canShowRecorderBanner,
      content: (
        <RecorderUpgradeBanner
          onClose={() => setIsRecorderBannerDismissed(true)}
        />
      ),
      className: '!bg-banner-green',
    },
    {
      key: 'webinar',
      condition: canShowWebinarBanner,
      content: <WebinarBanner onClose={onCloseWebinarBanner} />,
      className: '!bg-banner-green',
    },
  ];

  const notificationsList = bannerConfigs
    .filter((config) => config.condition)
    .map((config) => (
      <BannerSkeleton key={config.key} className={config.className}>
        {config.content}
      </BannerSkeleton>
    ));

  return <Caraousel itemsList={notificationsList} />;
};
